import React, { useState, useEffect, useRef, useCallback } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { 
  Video, 
  VideoOff, 
  Mic, 
  MicOff, 
  PhoneOff, 
  Users,
  Maximize2,
  Minimize2,
  Camera,
  MessageSquare,
  Hand,
  Settings,
  Volume2,
  VolumeX,
  RotateCcw,
  Wifi,
  WifiOff,
  Signal,
  SignalLow,
  SignalMedium,
  SignalHigh,
  AlertTriangle,
  Monitor,
  Layers,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Theta360Viewer from './Theta360Viewer';

/**
 * DualCamera360MeetingRoom - Meeting room with BOTH webcam AND 360° THETA camera
 * 
 * Key features:
 * - Host can share BOTH webcam (for face) AND THETA 360° (for room view)
 * - Main view: 360° panoramic view
 * - Sidebar: All participant webcams including host
 * - Remote participants visible as thumbnails on the right
 * - Bidirectional audio
 * - Network quality indicator
 */
const DualCamera360MeetingRoom = ({
  roomUrl,
  token,
  userName,
  isHost = false,
  thetaStream = null,
  onLeave,
  onError
}) => {
  // Daily.co call object
  const [callObject, setCallObject] = useState(null);
  const [callState, setCallState] = useState('idle');
  
  // Participants
  const [participants, setParticipants] = useState({});
  const [localParticipant, setLocalParticipant] = useState(null);
  
  // Local controls
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // 360° stream
  const [theta360Stream, setTheta360Stream] = useState(thetaStream);
  const [hostThetaStream, setHostThetaStream] = useState(null);
  
  // UI state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeView, setActiveView] = useState('360'); // '360' or 'grid'
  
  // Connection quality
  const [networkQuality, setNetworkQuality] = useState(null);
  const [networkStats, setNetworkStats] = useState(null);
  const networkStatsIntervalRef = useRef(null);
  
  // Refs
  const containerRef = useRef(null);
  const callObjectRef = useRef(null);
  const mountedRef = useRef(true);
  const hasInitializedRef = useRef(false);
  const localWebcamRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (callObjectRef.current) {
        try {
          callObjectRef.current.destroy();
        } catch (e) {
          console.log('Error destroying call object:', e);
        }
        callObjectRef.current = null;
      }
    };
  }, []);

  // Initialize Daily.co
  useEffect(() => {
    if (!roomUrl || !token || typeof token !== 'string') return;
    if (hasInitializedRef.current) return;
    
    hasInitializedRef.current = true;
    
    const initializeDaily = async () => {
      if (!mountedRef.current) return;
      
      try {
        const daily = DailyIframe.createCallObject({
          url: roomUrl,
          token: token,
          subscribeToTracksAutomatically: true,
          dailyConfig: {
            experimentalChromeVideoMuteLightOff: true
          }
        });

        if (!mountedRef.current) {
          daily.destroy();
          return;
        }

        callObjectRef.current = daily;
        
        // Event handlers
        daily.on('joining-meeting', () => {
          if (mountedRef.current) setCallState('joining');
        });
        daily.on('joined-meeting', handleJoinedMeeting);
        daily.on('left-meeting', handleLeftMeeting);
        daily.on('participant-joined', handleParticipantUpdate);
        daily.on('participant-updated', handleParticipantUpdate);
        daily.on('participant-left', handleParticipantLeft);
        daily.on('track-started', handleTrackStarted);
        daily.on('track-stopped', handleTrackStopped);
        daily.on('error', handleError);
        
        if (mountedRef.current) {
          setCallObject(daily);
          setCallState('joining');
          await daily.join({ userName });
        }
      } catch (err) {
        console.error('DualCamera360: Error:', err);
        hasInitializedRef.current = false;
        if (mountedRef.current) {
          setCallState('error');
          onError?.('Erreur lors de l\'initialisation: ' + (err.message || err));
        }
      }
    };

    initializeDaily();
  }, [roomUrl, token]);

  // Share THETA stream if host has it
  useEffect(() => {
    if (callObject && isHost && thetaStream && callState === 'joined') {
      // For host: we keep webcam on regular video track
      // THETA is sent as a custom video track or screen share
      shareThetaAsScreenShare();
    }
  }, [callObject, isHost, thetaStream, callState]);

  // Monitor network quality
  useEffect(() => {
    if (!callObject || callState !== 'joined') return;

    const handleNetworkQualityChange = (event) => {
      if (event.threshold) setNetworkQuality(event.threshold);
    };

    callObject.on('network-quality-change', handleNetworkQualityChange);

    const getNetworkStats = async () => {
      try {
        const stats = await callObject.getNetworkStats();
        if (stats && mountedRef.current) {
          setNetworkStats(stats);
          if (!networkQuality && stats.stats?.latest) {
            const { latest } = stats.stats;
            const avgPacketLoss = ((latest.videoRecvPacketLoss || 0) + (latest.audioRecvPacketLoss || 0)) / 2;
            let quality = 'good';
            if (avgPacketLoss > 10) quality = 'bad';
            else if (avgPacketLoss > 3) quality = 'low';
            setNetworkQuality(quality);
          }
        }
      } catch (err) {
        console.log('Error getting network stats:', err);
      }
    };

    getNetworkStats();
    networkStatsIntervalRef.current = setInterval(getNetworkStats, 3000);

    return () => {
      callObject.off('network-quality-change', handleNetworkQualityChange);
      if (networkStatsIntervalRef.current) clearInterval(networkStatsIntervalRef.current);
    };
  }, [callObject, callState]);

  const shareThetaAsScreenShare = async () => {
    if (!callObject || !thetaStream) return;
    
    try {
      // Start screen share with THETA stream
      const videoTrack = thetaStream.getVideoTracks()[0];
      if (videoTrack) {
        await callObject.startScreenShare({
          mediaStream: thetaStream
        });
        console.log('THETA 360° shared as screen share');
      }
    } catch (err) {
      console.error('Error sharing THETA:', err);
      // Fallback: replace main video with THETA
      try {
        const videoTrack = thetaStream.getVideoTracks()[0];
        await callObject.setInputDevicesAsync({ videoSource: videoTrack });
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
    }
  };

  const handleJoinedMeeting = () => {
    setCallState('joined');
    updateParticipants();
  };

  const handleLeftMeeting = () => {
    setCallState('left');
    onLeave?.();
  };

  const handleParticipantUpdate = () => updateParticipants();
  const handleParticipantLeft = () => updateParticipants();
  
  const handleTrackStarted = (event) => {
    updateParticipants();
    // Check for screen share (THETA 360°)
    if (event.participant && !event.participant.local && event.track?.kind === 'video') {
      checkForThetaStream(event.participant);
    }
  };

  const handleTrackStopped = () => updateParticipants();

  const handleError = (event) => {
    console.error('Daily.co error:', event);
    const errorMsg = event.errorMsg || event.error?.msg || '';
    const isFatal = event.action === 'error' && 
      (errorMsg.includes('not-allowed') || errorMsg.includes('invalid') || errorMsg.includes('expired'));
    
    if (isFatal) {
      setCallState('error');
      onError?.(errorMsg || 'Erreur de connexion');
    }
  };

  const updateParticipants = () => {
    if (!callObject) return;
    
    const allParticipants = callObject.participants();
    setParticipants(allParticipants);
    setLocalParticipant(allParticipants.local);
    
    // Check for host's screen share (THETA)
    Object.entries(allParticipants).forEach(([id, p]) => {
      if (id !== 'local' && p.screen) {
        checkForThetaStream(p);
      }
    });
  };

  const checkForThetaStream = (participant) => {
    // Check screen share track for THETA
    if (participant.tracks?.screenVideo?.persistentTrack) {
      const videoTrack = participant.tracks.screenVideo.persistentTrack;
      const stream = new MediaStream([videoTrack]);
      setHostThetaStream(stream);
    }
  };

  const toggleCamera = () => {
    if (!callObject) return;
    callObject.setLocalVideo(!isCameraOn);
    setIsCameraOn(!isCameraOn);
  };

  const toggleMic = () => {
    if (!callObject) return;
    callObject.setLocalAudio(!isMicOn);
    setIsMicOn(!isMicOn);
  };

  const leaveMeeting = () => {
    if (!callObject) return;
    callObject.leave();
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      // Ignore fullscreen errors (user may have exited manually)
      console.log('Fullscreen toggle error:', err.message);
      setIsFullscreen(!!document.fullscreenElement);
    }
  };

  const retryConnection = () => {
    hasInitializedRef.current = false;
    window.location.reload();
  };

  // Get remote participants
  const remoteParticipants = Object.entries(participants)
    .filter(([id]) => id !== 'local')
    .map(([id, p]) => ({ id, ...p }));

  // Determine active 360 stream (from host or local)
  const active360Stream = isHost ? thetaStream : hostThetaStream;

  // Network Quality Indicator
  const NetworkQualityIndicator = () => {
    if (callState !== 'joined') return null;
    
    if (!networkQuality && !networkStats) {
      return (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-700/50">
          <Wifi size={14} className="text-gray-400 animate-pulse" />
          <span className="text-xs text-gray-400">Connexion...</span>
        </div>
      );
    }

    let quality = networkQuality || 'good';
    let qualityLabel = 'Excellente';
    let qualityColor = 'text-green-400';
    let bgColor = 'bg-green-500/20';
    let bars = 4;

    if (quality === 'low' || quality === 'poor') {
      qualityLabel = 'Moyenne';
      qualityColor = 'text-yellow-400';
      bgColor = 'bg-yellow-500/20';
      bars = 2;
    } else if (quality === 'bad' || quality === 'very-low') {
      qualityLabel = 'Faible';
      qualityColor = 'text-red-400';
      bgColor = 'bg-red-500/20';
      bars = 1;
    } else if (quality === 'good') {
      qualityLabel = 'Bonne';
      bars = 3;
    }

    return (
      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${bgColor}`}>
        <div className="flex items-end gap-0.5 h-4">
          {[1, 2, 3, 4].map((bar) => (
            <div
              key={bar}
              className={`w-1 rounded-sm ${bar <= bars ? qualityColor.replace('text-', 'bg-') : 'bg-gray-600'}`}
              style={{ height: `${bar * 3 + 2}px` }}
            />
          ))}
        </div>
        <span className={`text-xs font-medium ${qualityColor}`}>{qualityLabel}</span>
      </div>
    );
  };

  // Loading states
  if (!token || typeof token !== 'string') {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] bg-gray-900 rounded-xl">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8] mx-auto mb-4"></div>
          <p>Préparation de la connexion...</p>
        </div>
      </div>
    );
  }

  if (callState === 'idle' || callState === 'joining') {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] bg-gray-900 rounded-xl">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8] mx-auto mb-4"></div>
          <p>Connexion à la salle de réunion...</p>
        </div>
      </div>
    );
  }

  if (callState === 'error') {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] bg-gray-900 rounded-xl">
        <div className="text-center text-white">
          <VideoOff size={48} className="mx-auto mb-4 text-red-400" />
          <p className="text-lg font-medium">Erreur de connexion</p>
          <button onClick={retryConnection} className="mt-4 px-4 py-2 bg-[#00D3A8] rounded-lg hover:bg-[#00B894]">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="bg-gray-900 rounded-xl overflow-hidden h-full flex flex-col"
      data-testid="dual-camera-360-meeting-room"
    >
      {/* Main content area */}
      <div className="relative flex-1 flex min-h-[300px]">
        
        {/* Main View - 360° panorama */}
        <div className={`relative flex-1 transition-all ${sidebarCollapsed ? 'mr-0' : 'mr-64'}`}>
          {active360Stream ? (
            <div className="absolute inset-0">
              <Theta360Viewer
                stream={active360Stream}
                isActive={true}
                className="w-full h-full"
                showControls={true}
              />
              {/* 360° badge */}
              <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
                <span className="px-3 py-1 bg-[#00D3A8] text-white text-sm font-medium rounded-full flex items-center gap-1">
                  <Camera size={14} />
                  Vue 360° Interactive
                </span>
              </div>
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
              <div className="text-center text-white">
                <Camera size={48} className="mx-auto mb-4 text-gray-500" />
                <p>En attente de la vue 360°...</p>
                <p className="text-sm text-gray-400 mt-2">
                  {isHost ? "Connectez votre caméra THETA" : "Le gestionnaire va partager la vue 360°"}
                </p>
              </div>
            </div>
          )}

          {/* View toggle buttons */}
          <div className="absolute top-4 right-4 flex gap-2 z-10">
            <button
              onClick={() => setActiveView('360')}
              className={`p-2 rounded-lg transition-colors ${
                activeView === '360' ? 'bg-[#00D3A8] text-white' : 'bg-black/50 text-white hover:bg-black/70'
              }`}
              title="Vue 360°"
            >
              <Camera size={18} />
            </button>
            <button
              onClick={() => setActiveView('grid')}
              className={`p-2 rounded-lg transition-colors ${
                activeView === 'grid' ? 'bg-[#00D3A8] text-white' : 'bg-black/50 text-white hover:bg-black/70'
              }`}
              title="Vue grille"
            >
              <Layers size={18} />
            </button>
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
          </div>
        </div>

        {/* Sidebar toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className={`absolute top-1/2 -translate-y-1/2 z-20 p-1 bg-gray-700 hover:bg-gray-600 text-white rounded-l-lg transition-all ${
            sidebarCollapsed ? 'right-0' : 'right-64'
          }`}
        >
          {sidebarCollapsed ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>

        {/* Right Sidebar - Participants webcams */}
        <div 
          className={`absolute top-0 right-0 h-full w-64 bg-gray-800/95 border-l border-gray-700 flex flex-col transition-transform ${
            sidebarCollapsed ? 'translate-x-full' : 'translate-x-0'
          }`}
        >
          {/* Sidebar header */}
          <div className="p-3 border-b border-gray-700">
            <h3 className="text-white font-medium flex items-center gap-2">
              <Users size={16} />
              Participants ({Object.keys(participants).length})
            </h3>
          </div>

          {/* Participants list */}
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {/* Local participant (you) */}
            {localParticipant && (
              <ParticipantCard 
                participant={localParticipant} 
                isLocal={true}
                isHost={isHost}
                label="Vous"
              />
            )}
            
            {/* Remote participants */}
            {remoteParticipants.map((p) => (
              <ParticipantCard 
                key={p.id} 
                participant={p}
                isHost={p.owner}
                label={p.user_name || 'Participant'}
              />
            ))}

            {/* Empty state */}
            {remoteParticipants.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <Users size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">En attente des autres participants...</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-t border-gray-700">
        {/* Left - Network quality */}
        <div className="flex items-center gap-2">
          <NetworkQualityIndicator />
          {isHost && active360Stream && (
            <span className="px-2 py-1 bg-[#00D3A8]/20 text-[#00D3A8] text-xs rounded-full flex items-center gap-1">
              <Camera size={12} />
              360° actif
            </span>
          )}
        </div>

        {/* Center - Main controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={toggleCamera}
            className={`p-3 rounded-full transition-colors ${
              isCameraOn ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
            title={isCameraOn ? 'Désactiver webcam' : 'Activer webcam'}
          >
            {isCameraOn ? <Video size={20} /> : <VideoOff size={20} />}
          </button>

          <button
            onClick={toggleMic}
            className={`p-3 rounded-full transition-colors ${
              isMicOn ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
            title={isMicOn ? 'Désactiver micro' : 'Activer micro'}
          >
            {isMicOn ? <Mic size={20} /> : <MicOff size={20} />}
          </button>

          <button
            onClick={leaveMeeting}
            className="p-3 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
            title="Quitter"
          >
            <PhoneOff size={20} />
          </button>
        </div>

        {/* Right - Toggle sidebar */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`p-2 rounded-lg transition-colors ${
              sidebarCollapsed ? 'bg-gray-700 text-gray-400' : 'bg-[#00D3A8]/20 text-[#00D3A8]'
            }`}
            title={sidebarCollapsed ? 'Afficher participants' : 'Masquer participants'}
          >
            <Users size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * ParticipantCard - Webcam card for sidebar
 */
const ParticipantCard = ({ participant, isLocal = false, isHost = false, label }) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const videoTrack = participant?.tracks?.video?.persistentTrack;
    if (videoRef.current && videoTrack) {
      videoRef.current.srcObject = new MediaStream([videoTrack]);
    }
  }, [participant?.tracks?.video?.persistentTrack]);

  useEffect(() => {
    const audioTrack = participant?.tracks?.audio?.persistentTrack;
    if (audioRef.current && audioTrack && !isLocal) {
      audioRef.current.srcObject = new MediaStream([audioTrack]);
    }
  }, [participant?.tracks?.audio?.persistentTrack, isLocal]);

  const hasVideo = participant?.video;
  const hasAudio = participant?.audio;

  return (
    <div className="bg-gray-700/50 rounded-lg overflow-hidden">
      {/* Video area */}
      <div className="relative aspect-video bg-gray-800">
        {hasVideo ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={isLocal}
            className={`w-full h-full object-cover ${isLocal ? 'transform scale-x-[-1]' : ''}`}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center text-white text-lg font-bold">
              {label?.charAt(0).toUpperCase()}
            </div>
          </div>
        )}

        {/* Status badges */}
        <div className="absolute top-1 right-1 flex gap-1">
          {!hasAudio && (
            <span className="p-1 bg-red-500/80 rounded" title="Micro désactivé">
              <MicOff size={10} className="text-white" />
            </span>
          )}
          {!hasVideo && (
            <span className="p-1 bg-red-500/80 rounded" title="Caméra désactivée">
              <VideoOff size={10} className="text-white" />
            </span>
          )}
        </div>

        {/* Host badge */}
        {isHost && (
          <div className="absolute top-1 left-1">
            <span className="px-1.5 py-0.5 bg-[#00D3A8] text-white text-[10px] rounded font-medium">
              Hôte
            </span>
          </div>
        )}
      </div>

      {/* Name label */}
      <div className="p-2 flex items-center justify-between">
        <span className="text-white text-sm truncate">{label}</span>
        <div className="flex items-center gap-1">
          {hasAudio ? (
            <Mic size={12} className="text-green-400" />
          ) : (
            <MicOff size={12} className="text-red-400" />
          )}
        </div>
      </div>

      {/* Audio element (hidden) */}
      {!isLocal && <audio ref={audioRef} autoPlay />}
    </div>
  );
};

export default DualCamera360MeetingRoom;
